import cx from 'classnames';
import { asset } from '../../../services/assetResolverService';
import { CSSProperties } from 'react';

export type TWowIconSize =
  | 'xxx-small'
  | 'xx-small'
  | 'x-small'
  | 'small'
  | 'medium'
  | 'large'
  | 'x-large'
  | 'small-medium';
export type TWowIconSizeProps = { fileSize: string; width: number; height: number };

export const getWowIconBiggerSize = (size: TWowIconSize): TWowIconSize => {
  switch (size) {
    case 'xxx-small':
      return 'xx-small';
    case 'xx-small':
      return 'x-small';
    case 'x-small':
      return 'small';
    case 'small':
      return 'small-medium';
    case 'small-medium':
      return 'medium';
    case 'medium':
      return 'large';
    case 'large':
    case 'x-large':
      return 'x-large';
  }
};

export type TWowIconProps = {
  size: TWowIconSize;
  icon: string;
  className?: string;
  style?: CSSProperties;
  imgStyle?: CSSProperties;
  imgClassName?: string;
  // special class to pass in to octagon shapes, when used
  octagonContainerClassName?: string;
  // when set will override the image url with a specific image
  // useful to ensure the same styling as a wow icon
  overrideImageUrl?: string;
  overrideSize?: TWowIconSizeProps;
  shape?: 'square' | 'circle' | 'octagon';
  lazy?: boolean;
};

const ICON_SIZE_PROPS: Record<TWowIconSize, TWowIconSizeProps> = {
  'xxx-small': {
    fileSize: 'small',
    width: 16,
    height: 16
  },
  'xx-small': {
    fileSize: 'small',
    width: 18,
    height: 18
  },
  'x-small': {
    fileSize: 'small',
    width: 20,
    height: 20
  },
  small: {
    fileSize: 'small',
    width: 26,
    height: 26
  },
  'small-medium': {
    fileSize: 'medium',
    width: 32,
    height: 32
  },
  medium: {
    fileSize: 'medium',
    width: 36,
    height: 36
  },
  large: {
    fileSize: 'large',
    width: 56,
    height: 56
  },
  'x-large': {
    fileSize: 'large',
    width: 70,
    height: 70
  }
};

// what is the next scale up from each size
const ICON_SCALEUP_SIZES: Record<TWowIconSize, string> = {
  'xxx-small': 'medium',
  'xx-small': 'medium',
  'x-small': 'medium',
  small: 'medium',
  'small-medium': 'medium',
  medium: 'large',
  large: 'large',
  'x-large': 'large'
};

// always tries to scale up the image used so it looks good on retina displays
export default function WowIcon(props: TWowIconProps) {
  const sizeProps = props.overrideSize || ICON_SIZE_PROPS[props.size];
  const scaledUpSize = ICON_SIZE_PROPS[ICON_SCALEUP_SIZES[props.size] as TWowIconSize] || sizeProps;

  const shape = props.shape || 'square';
  if (shape === 'octagon') {
    return (
      <div
        className={cx('d-inline-block', props.className)}
        style={{ ...props.style, width: sizeProps.width, height: sizeProps.height, verticalAlign: 'middle' }}
      >
        <div
          className={cx('rio-wow-icon--shape-octagon-container', props.octagonContainerClassName)}
          style={{ width: sizeProps.width, height: sizeProps.height }}
        >
          <img
            loading={props.lazy ? 'lazy' : undefined}
            width={sizeProps.width}
            height={sizeProps.height}
            className={cx(
              `rio-wow-icon rio-wow-icon--shape-${shape}`,
              {
                [`rio-wow-icon--${props.size}`]: !props.overrideSize
              },
              props.imgClassName
            )}
            src={props.overrideImageUrl || asset(`/images/wow/icons/${scaledUpSize.fileSize}/${props.icon}.jpg`)}
            style={{
              ...props.imgStyle,
              width: sizeProps.width - 4,
              height: sizeProps.height - 4
            }}
          />
        </div>
      </div>
    );
  } else {
    return (
      <div
        className={cx('d-inline-block', props.className)}
        style={{ ...props.style, width: sizeProps.width, height: sizeProps.height, verticalAlign: 'middle' }}
      >
        <img
          loading={props.lazy ? 'lazy' : undefined}
          width={sizeProps.width}
          height={sizeProps.height}
          className={cx(
            `rio-wow-icon rio-wow-icon--shape-${shape}`,
            {
              [`rio-wow-icon--${props.size}`]: !props.overrideSize
            },
            props.imgClassName
          )}
          src={props.overrideImageUrl || asset(`/images/wow/icons/${scaledUpSize.fileSize}/${props.icon}.jpg`)}
          style={{ ...props.imgStyle, display: 'block' }}
        />
      </div>
    );
  }
}
